import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import {
  Calendar, Users, Heart, HandHelping, Flame, Utensils,
  Send, ChevronRight, MapPin, Clock, Sparkles,
} from "lucide-react";

const TABS = [
  { key: "overview",   label: "Overview" },
  { key: "schedule",   label: "Schedule" },
  { key: "revival",    label: "Fall Revival" },
  { key: "reunion",    label: "Family Reunion" },
  { key: "volunteer",  label: "Volunteer" },
  { key: "register",   label: "Family Registration" },
];

const SCHEDULE = [
  { date: "Oct 4",  day: "Sunday",    title: "Fall Revival · Opening Services",   speaker: "Dr. Carl Solomon",       location: "Main Sanctuary", tag: "revival", time: "7:00 AM Virtual · 10:00 AM In-Person & Virtual" },
  { date: "Oct 5",  day: "Monday",    title: "Fall Revival · Night 2",            speaker: "Dr. Carl Solomon",       location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 6",  day: "Tuesday",   title: "Fall Revival · Night 3",            speaker: "Dr. Carl Solomon",       location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 7",  day: "Wednesday", title: "Fall Revival · Night 4",            speaker: "Dr. Carl Solomon",       location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 14", day: "Wednesday", title: "Wednesday Revival Service",         speaker: "Bishop Lisa Weah",       location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 21", day: "Wednesday", title: "Wednesday Revival Service",         speaker: "Rev. Kevin Swann",       location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 28", day: "Wednesday", title: "Wednesday Revival Service",         speaker: "Rev. Dr. Akeem Walker",  location: "Virtual",        tag: "revival", time: "7:00 PM" },
  { date: "Oct 30", day: "Friday",    title: "Family Reunion — Meet & Greet",     speaker: "DJ + Icebreakers",       location: "Fellowship Hall",tag: "reunion", time: "6:30–9:00 PM" },
  { date: "Oct 31", day: "Saturday",  title: "Family Reunion — Opening Session",  speaker: "Pastor Barber",          location: "Main Sanctuary", tag: "reunion", time: "9:00 AM" },
  { date: "Oct 31", day: "Saturday",  title: "Family Tables & Field Day",         speaker: "Various table leaders",  location: "Church Grounds", tag: "reunion" },
];

const SPEAKERS = [
  { name: "Dr. Carl Solomon",       role: "Fall Revival · Opening Week",   when: "Oct 4 – 7",         photo: "/images/flc/carl-solomon.png" },
  { name: "Bishop Lisa Weah",       role: "Wednesday Revival Service",     when: "Wed · Oct 14 · 7 PM", photo: "/images/flc/lisa-weah.png" },
  { name: "Rev. Kevin Swann",       role: "Wednesday Revival Service",     when: "Wed · Oct 21 · 7 PM", photo: "/images/flc/kevin-swann.png" },
  { name: "Rev. Dr. Akeem Walker",  role: "Wednesday Revival Service",     when: "Wed · Oct 28 · 7 PM", photo: "/images/flc/akeem-walker.png" },
];

const FAMILY_TABLES = ["Marriage", "Blended Families", "Singles", "Finances"];

const VOLUNTEER_AREAS = [
  "Setup / Teardown",
  "Hospitality",
  "Children's Area",
  "Kitchen",
  "Music / Media",
  "Registration",
  "Prayer Team",
  "Other",
];

export default function FamilyLifeConference() {
  const [tab, setTab] = useState("overview");
  return (
    <div data-testid="flc-page">
      <PageHero
        overline="Family Life Conference 2026"
        title={<>Stronger <em className="text-gold-light italic font-light">families</em>. Deeper faith.</>}
        kicker="A month of revival, prayer and family reconnection at Sixth Mount Zion — October 2026."
        image="/images/branding/congregation-family.jpg"
      />

      <Container className="pb-32">
        {/* Tab strip */}
        <div className="flex flex-wrap gap-1 border-b border-white/10 mb-12" data-testid="flc-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-bold transition-colors -mb-px border-b-2 ${
                tab === t.key ? "text-gold border-gold" : "text-white/55 border-transparent hover:text-white"
              }`}
              data-testid={`flc-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview"  && <Overview go={setTab} />}
        {tab === "schedule"  && <Schedule />}
        {tab === "revival"   && <RevivalTab />}
        {tab === "reunion"   && <ReunionTab />}
        {tab === "volunteer" && <VolunteerForm />}
        {tab === "register"  && <FamilyRegistrationForm />}
      </Container>
    </div>
  );
}

/* ============ OVERVIEW ============ */
function Overview({ go }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-12">
        {/* Intro + rail */}
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <Overline>Conference Overview</Overline>
            <h2 className="font-sans text-4xl md:text-5xl font-black leading-tight tracking-[-0.02em] mb-6">
              A month for <span className="brand-gradient-text">families</span> to gather, grow and go home stronger.
            </h2>
            <p className="text-lg text-white/85 leading-relaxed mb-4">
              The 2026 Family Life Conference brings four weeks of ministry designed for every kind of family — married couples, single parents, blended homes, grandparents raising grandchildren, and everyone in between.
            </p>
            <p className="text-white/70 leading-relaxed">
              The month opens with <strong className="text-gold">Fall Revival (Oct 4–7)</strong> led by Dr. Carl Solomon with Wednesday follow-up services featuring Bishop Lisa Weah, Rev. Kevin Swann, and Rev. Dr. Akeem Walker — closing with the <strong className="text-gold">Family Reunion (Oct 30–31)</strong>, two days of meet-and-greet, teaching, and field day.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 my-10">
              <FactCard icon={Flame}       label="Fall Revival" value="Oct 4–28" />
              <FactCard icon={Users}       label="Family Reunion" value="Oct 30–31" />
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={() => go("register")} className="btn-wine px-7 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2" data-testid="overview-register-cta">
                <Users className="h-4 w-4" /> Register Your Family
              </button>
              <button onClick={() => go("volunteer")} className="btn-gold px-7 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2" data-testid="overview-volunteer-cta">
                <HandHelping className="h-4 w-4" /> Volunteer
              </button>
              <button onClick={() => go("schedule")} className="btn-ghost px-7 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2 border border-white/20">
                <Calendar className="h-4 w-4" /> Full Schedule
              </button>
            </div>
          </div>

          {/* RIGHT RAIL */}
          <div className="lg:col-span-4">
            <div className="glass p-8 sticky top-28">
              <Overline>Fall Revival Speakers</Overline>
              <ul className="space-y-4 mt-4">
                {SPEAKERS.map((s) => (
                  <li key={s.name} className="flex items-center gap-4" data-testid={`flc-rail-speaker-${s.name.replace(/\W+/g, '-').toLowerCase()}`}>
                    <img
                      src={s.photo}
                      alt={s.name}
                      loading="lazy"
                      className="h-14 w-14 rounded-full object-cover border border-gold/40 bg-wine-900/40 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-serif text-base font-bold leading-tight truncate">{s.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gold mt-1">{s.when}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/10 mt-6 pt-6">
                <Overline>Host</Overline>
                <div className="font-serif text-xl font-bold">Rev. Jerome A. Barber</div>
                <div className="text-xs text-white/55">Senior Pastor · Sixth Mount Zion</div>
              </div>
            </div>
          </div>
        </div>

        {/* MEET THE SPEAKERS — full width visual row */}
        <div className="mt-16 border-t border-white/10 pt-12" data-testid="flc-speakers-section">
          <Overline>Meet the Speakers</Overline>
          <h3 className="font-sans text-3xl md:text-4xl font-black tracking-[-0.02em] mb-8">
            A line-up of <span className="brand-gradient-text-warm">anointed voices</span>.
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {SPEAKERS.map((s) => (
              <div
                key={s.name}
                className="glass p-5 flex flex-col items-center text-center"
                data-testid={`flc-speaker-card-${s.name.replace(/\W+/g, '-').toLowerCase()}`}
              >
                <div className="w-full aspect-square overflow-hidden rounded-full bg-wine-900/40 border border-gold/40 mb-4">
                  <img
                    src={s.photo}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="font-serif text-lg font-bold leading-tight">{s.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-gold mt-2">{s.role}</div>
                <div className="text-xs text-white/60 mt-1">{s.when}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="glass p-5">
      <Icon className="h-5 w-5 text-gold mb-3" />
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
      <div className="font-serif text-xl font-black mt-1">{value}</div>
    </div>
  );
}

/* ============ SCHEDULE ============ */
function Schedule() {
  return (
    <div>
      <Overline>Full Schedule</Overline>
      <h2 className="font-sans text-3xl md:text-4xl font-black mb-8 tracking-[-0.02em]">Every night. Every family.</h2>
      <div className="space-y-px bg-white/5">
        {SCHEDULE.map((e, i) => (
          <div key={i} className="bg-wine-950 p-6 grid md:grid-cols-12 gap-4 items-center" data-testid={`flc-event-${i}`}>
            <div className="md:col-span-2">
              <div className="font-serif text-3xl font-black text-gold">{e.date}</div>
              <div className="text-xs uppercase tracking-widest text-white/50">{e.day}</div>
            </div>
            <div className="md:col-span-7">
              <div className="text-[10px] uppercase tracking-widest text-gold mb-1">{e.tag}</div>
              <div className="font-serif text-xl font-bold">{e.title}</div>
              {e.speaker && <div className="text-sm text-white/60 mt-1">Speaker: {e.speaker}</div>}
            </div>
            <div className="md:col-span-3 text-sm text-white/60 space-y-1">
              {e.time && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold/60" /> {e.time}</div>}
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/60" /> {e.location}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ REVIVAL ============ */
function RevivalTab() {
  const revivalEvents = SCHEDULE.filter((s) => s.tag === "revival");
  return (
    <div>
      <Overline>Fall Revival · Oct 4 – 28</Overline>
      <h2 className="font-sans text-4xl md:text-5xl font-black leading-tight tracking-[-0.02em] mb-6">
        A month of <span className="brand-gradient-text">Spirit-filled</span> preaching.
      </h2>
      <p className="text-lg text-white/85 leading-relaxed mb-8 max-w-3xl">
        Fall Revival opens with <strong className="text-gold">Dr. Carl Solomon</strong> for a week of services, followed by three Wednesday follow-up nights with guest preachers. Bring your whole household — every night a fresh word.
      </p>

      <div className="space-y-px bg-white/5 mb-10">
        {revivalEvents.map((e, i) => (
          <div key={i} className="bg-wine-950 p-6 grid md:grid-cols-12 gap-4 items-center" data-testid={`revival-event-${i}`}>
            <div className="md:col-span-2">
              <div className="font-serif text-2xl font-black text-gold">{e.date}</div>
              <div className="text-xs uppercase tracking-widest text-white/50">{e.day}</div>
            </div>
            <div className="md:col-span-6">
              <div className="font-serif text-lg font-bold">{e.title}</div>
              <div className="text-sm text-white/60 mt-1">Speaker: <strong className="text-white/85">{e.speaker}</strong></div>
            </div>
            <div className="md:col-span-4 text-sm text-white/60 space-y-1">
              {e.time && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold/60" /> {e.time}</div>}
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/60" /> {e.location}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ REUNION ============ */
function ReunionTab() {
  return (
    <div>
      <Overline>Family Reunion · Oct 30 – 31</Overline>
      <h2 className="font-sans text-4xl md:text-5xl font-black leading-tight tracking-[-0.02em] mb-8">
        Come home to <span className="brand-gradient-text">family</span>.
      </h2>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Friday */}
        <div className="glass p-8">
          <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-2">Friday · Oct 30 · 6:30 – 9:00 PM</div>
          <h3 className="font-serif text-2xl font-bold mb-4">Meet &amp; Greet Night</h3>
          <p className="text-white/75 mb-5 leading-relaxed">A relaxed icebreaker evening — music, DJ, family games, and reconnecting with the SMZBT family.</p>
          <div className="border-l-2 border-gold pl-5 py-4 bg-gold/[0.06]">
            <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-2">Family Color Challenge</div>
            <p className="text-white/85 text-sm leading-relaxed">
              Every family picks a signature color and everyone wears it Friday night. Enter your color when you register — we track colors so no two families end up matching.
            </p>
          </div>
        </div>

        {/* Saturday */}
        <div className="glass p-8">
          <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-2">Saturday · Oct 31</div>
          <h3 className="font-serif text-2xl font-bold mb-4">Opening Session · Family Tables · Field Day</h3>
          <p className="text-white/75 mb-5 leading-relaxed">The main event day — opens with Pastor Barber, then breaks into Family Tables, and closes with a cookout and field day.</p>
          <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-3">Family Table topics (in progress)</div>
          <div className="flex flex-wrap gap-2">
            {FAMILY_TABLES.map((t) => (
              <span key={t} className="text-xs uppercase tracking-wider bg-wine-700/40 border border-wine-600/40 px-3 py-1.5 text-white/85 font-semibold">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeakerCard({ name, role, note }) {
  return (
    <div className="lg:col-span-4">
      <div className="glass p-6 sticky top-28">
        <Overline>Speaker</Overline>
        <div className="font-serif text-xl font-bold">{name}</div>
        <div className="text-xs uppercase tracking-widest text-gold mb-3">{role}</div>
        <p className="text-sm text-white/70 leading-relaxed">{note}</p>
      </div>
    </div>
  );
}

/* ============ VOLUNTEER FORM ============ */
function VolunteerForm() {
  const EMPTY = { name: "", email: "", phone: "", preferred_areas: [], availability: "", comments: "" };
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (a) => setForm((f) => ({ ...f, preferred_areas: f.preferred_areas.includes(a) ? f.preferred_areas.filter((x) => x !== a) : [...f.preferred_areas, a] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([k, v]) => k === "preferred_areas" || v));
      await api.post("/flc/volunteer", payload);
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (submitted) return <ThankYou title="You're on the team." message="Thank you for stepping up. Our conference lead will reach out with next steps." onAgain={() => { setForm(EMPTY); setSubmitted(false); }} />;

  return (
    <form onSubmit={submit} className="glass p-10 space-y-8 max-w-3xl" data-testid="flc-volunteer-form">
      <div>
        <Overline>Volunteer Sign-Up</Overline>
        <h2 className="font-sans text-3xl md:text-4xl font-black tracking-[-0.02em]">Lend a hand. Serve a family.</h2>
        <p className="text-white/60 mt-2 text-sm">Pick your preferred area(s). We'll place you where you fit best.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
        <Field label="Name" required><input required data-testid="vol-name" value={form.name} onChange={u("name")} className={inputCls} /></Field>
        <Field label="Email" required><input required type="email" data-testid="vol-email" value={form.email} onChange={u("email")} className={inputCls} /></Field>
        <Field label="Phone" required className="md:col-span-2"><input required data-testid="vol-phone" value={form.phone} onChange={u("phone")} className={inputCls} placeholder="(757) 555-0100" /></Field>
      </div>
      <Field label="Preferred Volunteer Area(s)" required>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="vol-areas">
          {VOLUNTEER_AREAS.map((a) => (
            <button type="button" key={a} onClick={() => toggleArea(a)} className={`text-left px-4 py-3 border transition-colors text-sm font-bold ${form.preferred_areas.includes(a) ? "border-gold bg-gold/10 text-gold" : "border-white/15 hover:border-white/40 text-white/80"}`}>
              {a}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Availability" hint="Which days/nights are you available?"><input data-testid="vol-availability" value={form.availability} onChange={u("availability")} className={inputCls} placeholder="e.g. Oct 4–7 evenings, Oct 30–31 all day" /></Field>
      <Field label="Additional Comments"><textarea rows={3} data-testid="vol-comments" value={form.comments} onChange={u("comments")} className={inputCls} /></Field>
      <div className="flex justify-end pt-4 border-t border-white/10">
        <button type="submit" disabled={busy} className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="vol-submit">
          <Send className="h-4 w-4" /> {busy ? "Submitting…" : "Sign Me Up"}
        </button>
      </div>
    </form>
  );
}

/* ============ FAMILY REGISTRATION FORM ============ */
function FamilyRegistrationForm() {
  const EMPTY = {
    family_name: "", family_color: "", primary_contact_name: "", email: "", phone: "",
    adults_count: 1, children_count: 0, attendance: "both", notes: "",
  };
  const [form, setForm] = useState(EMPTY);
  const [taken, setTaken] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/flc/family-colors").then((r) => setTaken(r.data || [])).catch(() => {});
  }, []);

  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const colorLower = (form.family_color || "").trim().toLowerCase();
  const colorClash = taken.find((t) => (t.family_color || "").trim().toLowerCase() === colorLower && colorLower);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.attendance) { toast.error("Please choose which day(s) you'll attend."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        adults_count: parseInt(form.adults_count, 10) || 0,
        children_count: parseInt(form.children_count, 10) || 0,
      };
      await api.post("/flc/family-registration", payload);
      setSubmitted(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (submitted) return <ThankYou title="Your family is registered!" message={`See you at the Family Life Conference. This form was for planning — there's no ticket needed, just show up.`} onAgain={() => { setForm(EMPTY); setSubmitted(false); }} />;

  const total = (parseInt(form.adults_count, 10) || 0) + (parseInt(form.children_count, 10) || 0);

  return (
    <form onSubmit={submit} className="glass p-10 space-y-8 max-w-3xl" data-testid="flc-registration-form">
      <div>
        <Overline>Family Registration</Overline>
        <h2 className="font-sans text-3xl md:text-4xl font-black tracking-[-0.02em]">Save your family a seat.</h2>
        <p className="text-white/60 mt-2 text-sm">
          <strong className="text-gold">Free.</strong> This is a planning form so we can prepare food, seating and activities — no ticket needed.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
        <Field label="Family / Household Name" required><input required data-testid="fam-name" value={form.family_name} onChange={u("family_name")} className={inputCls} placeholder="The Barber Family" /></Field>
        <Field label="Family Team Color" required hint={colorClash ? `⚠️ "${form.family_color}" is already claimed by ${colorClash.family_name}. Pick a different shade so families don't match.` : "Every family picks a color for Friday night — one color per family."}>
          <input required data-testid="fam-color" value={form.family_color} onChange={u("family_color")} className={`${inputCls} ${colorClash ? "text-red-300" : ""}`} placeholder="Royal Blue" />
        </Field>
        <Field label="Primary Contact" required><input required data-testid="fam-contact" value={form.primary_contact_name} onChange={u("primary_contact_name")} className={inputCls} /></Field>
        <Field label="Email" required><input required type="email" data-testid="fam-email" value={form.email} onChange={u("email")} className={inputCls} /></Field>
        <Field label="Phone" required className="md:col-span-2"><input required data-testid="fam-phone" value={form.phone} onChange={u("phone")} className={inputCls} /></Field>
        <Field label="# of Adults"><input type="number" min="0" data-testid="fam-adults" value={form.adults_count} onChange={u("adults_count")} className={inputCls} /></Field>
        <Field label="# of Children"><input type="number" min="0" data-testid="fam-children" value={form.children_count} onChange={u("children_count")} className={inputCls} /></Field>
      </div>

      <div className="flex items-center gap-3 text-sm text-white/70 -mt-3">
        <Utensils className="h-4 w-4 text-gold" /> Total attending: <strong className="text-gold-light">{total}</strong>
      </div>

      <Field label="Which days will you attend?" required>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="fam-attendance">
          {[
            { k: "friday",   label: "Friday Only",  sub: "Oct 30 · Meet & Greet" },
            { k: "saturday", label: "Saturday Only",sub: "Oct 31 · Main Day" },
            { k: "both",     label: "Both Days",    sub: "Full Family Reunion" },
          ].map((o) => (
            <button
              type="button"
              key={o.k}
              onClick={() => setForm((f) => ({ ...f, attendance: o.k }))}
              className={`text-left p-4 border transition-colors ${form.attendance === o.k ? "border-gold bg-gold/10 text-gold" : "border-white/15 hover:border-white/40 text-white/80"}`}
              data-testid={`fam-attendance-${o.k}`}
            >
              <div className="text-sm font-bold">{o.label}</div>
              <div className="text-xs text-white/50 mt-1">{o.sub}</div>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Optional Notes"><textarea rows={3} data-testid="fam-notes" value={form.notes} onChange={u("notes")} className={inputCls} placeholder="Dietary needs, accessibility, kids' ages..." /></Field>

      {taken.length > 0 && (
        <div className="border-t border-white/10 pt-6">
          <div className="text-[10px] uppercase tracking-widest text-white/45 font-bold mb-3">Colors already claimed</div>
          <div className="flex flex-wrap gap-2">
            {taken.map((t, i) => (
              <span key={i} className="text-xs bg-wine-700/40 border border-wine-600/40 px-3 py-1.5 text-white/80" data-testid={`taken-color-${i}`}>
                {t.family_color} · {t.family_name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-white/10">
        <button type="submit" disabled={busy} className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="fam-submit">
          <Send className="h-4 w-4" /> {busy ? "Registering…" : "Register My Family"}
        </button>
      </div>

      {/* T-SHIRT PURCHASE — separate vendor */}
      <TShirtPurchase />
    </form>
  );
}

/* ============ T-SHIRT PURCHASE (3rd party — Karl Gillis) ============ */
function TShirtPurchase() {
  return (
    <div className="border-t border-white/10 pt-10 mt-10" data-testid="flc-tshirt-block">
      <Overline>Conference T-Shirt</Overline>
      <h3 className="font-sans text-2xl md:text-3xl font-black tracking-[-0.02em] mb-3">
        Grab your <span className="brand-gradient-text-warm">conference tee</span>.
      </h3>
      <p className="text-sm text-white/70 mb-6 max-w-2xl">
        Family Life Conference shirts are available for purchase directly from Karl Gillis via Cash App.
      </p>

      <div className="grid md:grid-cols-5 gap-6 items-start">
        {/* Product sheet image — front, back, sides, neck tag */}
        <div className="md:col-span-2">
          <div
            className="aspect-square bg-wine-900/40 border border-white/10 overflow-hidden flex items-center justify-center"
            data-testid="flc-tshirt-image"
          >
            <img
              src="/images/flc/tshirt.jpg"
              alt="Family Life Conference 2026 T-Shirt — front, back, side, and neck tag views"
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mt-2 text-center">Family Life Conference 2026 Tee · Purple</div>
        </div>

        {/* Purchase details */}
        <div className="md:col-span-3 space-y-5">
          <div className="glass p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="px-4 py-1.5 rounded-full bg-[#00D632] text-black text-xs font-extrabold tracking-wider">CASH APP</div>
              <span className="text-xs uppercase tracking-widest text-white/50">How to pay</span>
            </div>
            <div className="space-y-1 text-white/90">
              <div className="font-sans text-xl font-extrabold brand-gradient-text-warm" data-testid="flc-tshirt-cashtag">$GodAboveAllThings</div>
              <div className="text-sm text-white/75">Karl Gillis</div>
              <div className="text-sm text-white/75">(757) 503-3157</div>
            </div>
            <a
              href="https://cash.app/$GodAboveAllThings"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wine inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold"
              data-testid="flc-tshirt-cashapp-btn"
            >
              Open Cash App
            </a>
          </div>

          <div className="border-l-2 border-gold/60 pl-4 py-2 text-xs text-white/55 leading-relaxed" data-testid="flc-tshirt-disclaimer">
            <strong className="text-gold/90 uppercase tracking-widest">Disclaimer:</strong> 6MZ, its representatives or employees do not maintain any business relationship with Karl Gillis. Therefore, 6MZ is not subject to any rights or obligations arising from the purchase of shirts from Karl Gillis. All t-shirt concerns must be directed to Karl Gillis.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ Shared helpers ============ */
const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";

function Field({ label, required, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">
        {label}{required && <span className="text-gold ml-1">*</span>}
      </div>
      {children}
      {hint && <p className={`text-xs mt-2 ${String(hint).startsWith("⚠") ? "text-red-300" : "text-white/45"}`}>{hint}</p>}
    </label>
  );
}

function ThankYou({ title, message, onAgain }) {
  return (
    <div className="glass p-12 md:p-16 text-center max-w-3xl mx-auto">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/15 border border-gold/40 mb-6">
        <Heart className="h-9 w-9 text-gold" />
      </div>
      <Overline>Received</Overline>
      <h3 className="font-sans text-4xl md:text-5xl font-black mb-6 tracking-[-0.02em]"><span className="brand-gradient-text">{title}</span></h3>
      <p className="text-white/80 max-w-2xl mx-auto leading-relaxed text-lg">{message}</p>
      <div className="mt-8">
        <button onClick={onAgain} className="btn-wine px-7 py-3.5 rounded-full text-sm uppercase tracking-widest font-bold" data-testid="flc-submit-another">Submit Another</button>
      </div>
    </div>
  );
}
